import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

// Pre-computed base classes for performance
const ALERT_BASE_CLASSES = "relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground";
const ALERT_DEFAULT_CLASSES = "bg-background text-foreground";
const ALERT_DESTRUCTIVE_CLASSES = "border-destructive/50 text-destructive dark:border-destructive [&>svg]:text-destructive";
const ALERT_TITLE_BASE_CLASSES = "mb-1 font-medium leading-none tracking-tight";
const ALERT_DESCRIPTION_BASE_CLASSES = "text-sm [&_p]:leading-relaxed";

const alertVariants = cva(
  ALERT_BASE_CLASSES,
  {
    variants: {
      variant: {
        default: ALERT_DEFAULT_CLASSES,
        destructive: ALERT_DESTRUCTIVE_CLASSES,
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

const Alert = React.memo(React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => {
  const memoizedClassName = React.useMemo(() => 
    cn(alertVariants({ variant }), className), 
    [variant, className]
  );

  return (
    <div
      ref={ref}
      role="alert"
      className={memoizedClassName}
      {...props}
    />
  );
}));
Alert.displayName = "Alert"

const AlertTitle = React.memo(React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => {
  const memoizedClassName = React.useMemo(() => 
    cn(ALERT_TITLE_BASE_CLASSES, className), 
    [className]
  );

  return (
    <h5
      ref={ref}
      className={memoizedClassName}
      {...props}
    />
  );
}));
AlertTitle.displayName = "AlertTitle"

const AlertDescription = React.memo(React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const memoizedClassName = React.useMemo(() => 
    cn(ALERT_DESCRIPTION_BASE_CLASSES, className), 
    [className]
  );

  return (
    <div
      ref={ref}
      className={memoizedClassName}
      {...props}
    />
  );
}));
AlertDescription.displayName = "AlertDescription"

export { Alert, AlertTitle, AlertDescription }
